import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    X, ChevronLeft, ChevronRight, MapPin, Calendar, ArrowRight,
    Sparkles, Quote, Filter, Link as LinkIcon, MessageCircle, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";
import { useSetting } from "@/context/CmsContext";
import InlineCTA from "@/components/InlineCTA";
import useSeo from "@/lib/useSeo";

/* ------------------------------------------------------------------ */
/*  Categories mapping (matches backend ALLOWED_EVENT_CATEGORIES)     */
/* ------------------------------------------------------------------ */
const CATEGORIES = [
    { id: "all",       label_pt: "Tudo",             label_en: "All",              emoji: "🎯" },
    { id: "bebidas",   label_pt: "Bebidas & Bar",    label_en: "Drinks & Bar",     emoji: "🍺" },
    { id: "cafe",      label_pt: "Café & Padaria",   label_en: "Coffee & Bakery",  emoji: "☕" },
    { id: "gelados",   label_pt: "Gelados & Doces",  label_en: "Ice cream & Sweets", emoji: "🍦" },
    { id: "comida",    label_pt: "Comida",           label_en: "Food",             emoji: "🍣" },
    { id: "estrutura", label_pt: "Estrutura base",   label_en: "Base structure",   emoji: "🛒" },
];

export default function Galeria() {
    const { tr } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState("all");
    const [counts, setCounts] = useState({});
    const [brandFilter, setBrandFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [lightbox, setLightbox] = useState(null); // index within filtered
    const [searchParams, setSearchParams] = useSearchParams();

    useSeo({
        title: tr(
            "Galeria — Casos reais de carrinhos NOVIFOOD",
            "Gallery — Real NOVIFOOD cart cases"
        ),
        description: tr(
            "Explore casos reais de branding em carrinhos NOVIFOOD: bebidas, café, gelados, comida e estrutura base. Uma referência visual para inspirar o seu próximo projecto.",
            "Explore real NOVIFOOD cart branding cases: drinks, coffee, ice cream, food and base structure. Visual references to inspire your next project."
        ),
        path: "/galeria",
    });

    /* --------------- Fetch ---------------- */
    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/events?limit=100").then(r => r.data || []),
            api.get("/events/categories").then(r => r.data?.counts || {}).catch(() => ({})),
        ])
            .then(([evs, cnts]) => {
                if (!alive) return;
                setItems(evs);
                setCounts(cnts);
            })
            .catch(() => alive && setItems([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    /* --------------- Derived options for advanced filters ---------------- */
    const availableBrands = useMemo(() => {
        const set = new Set(items.map(e => e.brand).filter(Boolean));
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [items]);

    const availableYears = useMemo(() => {
        const set = new Set(items.map(e => e.year).filter(Boolean));
        return Array.from(set).sort().reverse();
    }, [items]);

    /* --------------- Filtering ---------------- */
    const filtered = useMemo(() => {
        return items.filter(e => {
            if (active !== "all" && e.category !== active) return false;
            if (brandFilter && e.brand !== brandFilter) return false;
            if (yearFilter && e.year !== yearFilter) return false;
            return true;
        });
    }, [items, active, brandFilter, yearFilter]);

    const total = items.length;
    const activeFiltersCount = (active !== "all" ? 1 : 0) + (brandFilter ? 1 : 0) + (yearFilter ? 1 : 0);

    /* --------------- Deep-link (?item=slug) support ---------------- */
    // Once items loaded, if URL has ?item=slug and it exists, open lightbox.
    useEffect(() => {
        if (loading || !items.length) return;
        const slug = searchParams.get("item");
        if (!slug) return;
        // First try in filtered; if not there, drop the category to find it in items.
        let idx = filtered.findIndex(e => e.slug === slug);
        if (idx === -1) {
            // Reset filters so the item is visible and index matches.
            const match = items.find(e => e.slug === slug);
            if (!match) return;
            setActive("all");
            setBrandFilter("");
            setYearFilter("");
            // The next effect run (after `filtered` recomputes) will re-open.
            return;
        }
        setLightbox(idx);
    }, [loading, items, filtered, searchParams]);

    /* --------------- Lightbox handlers ---------------- */
    const openLightbox = useCallback((idx) => {
        setLightbox(idx);
        const item = filtered[idx];
        if (item?.slug) {
            const next = new URLSearchParams(searchParams);
            next.set("item", item.slug);
            setSearchParams(next, { replace: false });
        }
    }, [filtered, searchParams, setSearchParams]);

    const closeLightbox = useCallback(() => {
        setLightbox(null);
        if (searchParams.get("item")) {
            const next = new URLSearchParams(searchParams);
            next.delete("item");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const prev = useCallback(() => {
        setLightbox(i => {
            if (i === null) return null;
            const newIdx = (i - 1 + filtered.length) % filtered.length;
            const item = filtered[newIdx];
            if (item?.slug) {
                const next = new URLSearchParams(searchParams);
                next.set("item", item.slug);
                setSearchParams(next, { replace: true });
            }
            return newIdx;
        });
    }, [filtered, searchParams, setSearchParams]);

    const next = useCallback(() => {
        setLightbox(i => {
            if (i === null) return null;
            const newIdx = (i + 1) % filtered.length;
            const item = filtered[newIdx];
            if (item?.slug) {
                const np = new URLSearchParams(searchParams);
                np.set("item", item.slug);
                setSearchParams(np, { replace: true });
            }
            return newIdx;
        });
    }, [filtered, searchParams, setSearchParams]);

    useEffect(() => {
        if (lightbox === null) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [lightbox, closeLightbox, prev, next]);

    const current = lightbox !== null ? filtered[lightbox] : null;

    /* --------------- Share handlers ---------------- */
    const buildShareUrl = useCallback((slug) => {
        if (typeof window === "undefined") return "";
        return `${window.location.origin}/galeria?item=${slug}`;
    }, []);

    const [copiedSlug, setCopiedSlug] = useState(null);
    const copyLink = useCallback(async (slug) => {
        const url = buildShareUrl(slug);
        try {
            await navigator.clipboard.writeText(url);
            setCopiedSlug(slug);
            toast.success(tr("Link copiado!", "Link copied!"), {
                description: url,
                duration: 2500,
            });
            setTimeout(() => setCopiedSlug(null), 2000);
        } catch {
            toast.error(tr("Não foi possível copiar o link.", "Could not copy the link."));
        }
    }, [buildShareUrl, tr]);

    const shareOnWhatsApp = useCallback((item) => {
        const url = buildShareUrl(item.slug);
        const text = encodeURIComponent(
            `${item.brand}${item.title ? ` · ${item.title}` : ""} — ${url}`
        );
        window.open(`https://wa.me/${whatsappNumber}?text=${text}`, "_blank", "noopener,noreferrer");
    }, [buildShareUrl, whatsappNumber]);

    const resetFilters = () => {
        setActive("all");
        setBrandFilter("");
        setYearFilter("");
    };

    return (
        <>
            {/* HERO */}
            <section
                data-testid="galeria-hero"
                className="relative overflow-hidden border-b border-border gradient-navy"
            >
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -right-32 h-[520px] w-[520px] rounded-full opacity-20 pointer-events-none"
                    style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }}
                />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        {tr("Galeria", "Gallery")}
                    </span>
                    <h1 className="mt-3 max-w-4xl display-xl">
                        {tr("Uma ideia por ", "One idea per ")}
                        <span className="serif-accent text-secondary">{tr("carrinho", "cart")}</span>
                        {tr(".", ".")}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {tr(
                            "Cada carrinho é uma marca em movimento. Explore inspirações reais por categoria.",
                            "Each cart is a brand on the move. Browse real inspirations by category."
                        )}
                    </p>
                </div>
            </section>

            {/* FILTERS BAR */}
            <section
                data-testid="galeria-filters"
                className="sticky top-16 z-30 border-b border-border bg-white/95 backdrop-blur-md"
            >
                <div className="container-x py-4 space-y-3">
                    {/* Category pills */}
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
                        {CATEGORIES.map((cat) => {
                            const n = cat.id === "all" ? total : (counts[cat.id] || 0);
                            const isActive = active === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => setActive(cat.id)}
                                    data-testid={`galeria-cat-${cat.id}`}
                                    className={
                                        "shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition " +
                                        (isActive
                                            ? "border-primary bg-primary text-white shadow-sm"
                                            : "border-border bg-white text-primary hover:border-primary/40 hover:bg-muted")
                                    }
                                >
                                    <span aria-hidden>{cat.emoji}</span>
                                    <span>{tr(cat.label_pt, cat.label_en)}</span>
                                    <span
                                        className={
                                            "inline-flex items-center justify-center min-w-[22px] px-1.5 h-5 rounded-full text-[10px] font-extrabold " +
                                            (isActive ? "bg-secondary text-primary" : "bg-muted text-primary/70")
                                        }
                                    >
                                        {n}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Advanced dropdowns: brand + year */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                            <Filter className="h-3.5 w-3.5" />
                            {tr("Filtros", "Filters")}
                        </div>
                        <select
                            value={brandFilter}
                            onChange={(e) => setBrandFilter(e.target.value)}
                            data-testid="galeria-brand-filter"
                            className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary outline-none hover:border-primary/40 focus:border-primary"
                        >
                            <option value="">{tr("Todas as marcas", "All brands")}</option>
                            {availableBrands.map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                        <select
                            value={yearFilter}
                            onChange={(e) => setYearFilter(e.target.value)}
                            data-testid="galeria-year-filter"
                            className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary outline-none hover:border-primary/40 focus:border-primary"
                        >
                            <option value="">{tr("Todos os anos", "All years")}</option>
                            {availableYears.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        {activeFiltersCount > 0 && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                data-testid="galeria-clear-filters"
                                className="inline-flex items-center gap-1 rounded-full border border-transparent bg-muted px-3 py-1.5 text-xs font-bold text-primary hover:bg-zinc-200 transition"
                            >
                                <X className="h-3 w-3" />
                                {tr("Limpar", "Clear")} ({activeFiltersCount})
                            </button>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground">
                            {filtered.length} {filtered.length === 1 ? tr("resultado", "result") : tr("resultados", "results")}
                        </span>
                    </div>
                </div>
            </section>

            {/* MASONRY GRID */}
            <section className="container-x section-y" data-testid="galeria-grid-section">
                {loading ? (
                    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [column-fill:_balance]">
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <div
                                key={i}
                                className="mb-5 break-inside-avoid rounded-2xl bg-white border border-border animate-pulse"
                                style={{ height: 260 + (i % 3) * 60 }}
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-24">
                        <p className="text-muted-foreground text-lg">
                            {tr("Nenhum caso corresponde aos filtros seleccionados.", "No case matches the current filters.")}
                        </p>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="mt-6 inline-flex items-center gap-2 text-primary font-bold hover:underline"
                        >
                            {tr("Limpar filtros e ver tudo", "Clear filters and see everything")} <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <div
                        data-testid="galeria-grid"
                        className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [column-fill:_balance]"
                    >
                        {filtered.map((ev, idx) => (
                            <GalleryCard
                                key={ev.id}
                                ev={ev}
                                onOpen={() => openLightbox(idx)}
                            />
                        ))}
                    </div>
                )}

                {/* Inline CTA after grid — nudge conversion */}
                {!loading && filtered.length > 4 && (
                    <div className="mt-14">
                        <InlineCTA
                            eyebrow={tr("A sua vez", "Your turn")}
                            title={tr("Configura o teu carrinho — briefing gratuito em 30 min.", "Configure your cart — free 30-min briefing.")}
                            to="/simulador"
                            label={tr("Abrir simulador", "Open configurator")}
                            variant="gold"
                            icon={Sparkles}
                            testId="galeria-inline-cta-simulador"
                        />
                    </div>
                )}
            </section>

            {/* CTA */}
            <section className="border-t border-border bg-muted" data-testid="galeria-cta">
                <div className="container-x py-10 lg:py-12 text-center max-w-3xl mx-auto">
                    <Sparkles className="h-8 w-8 text-secondary mx-auto" />
                    <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-extrabold text-primary leading-tight">
                        {tr("A sua marca também pode ", "Your brand can also ")}
                        <span className="serif-accent text-secondary">
                            {tr("rodar", "roll")}
                        </span>
                        {tr(".", ".")}
                    </h2>
                    <p className="mt-4 text-muted-foreground">
                        {tr(
                            "Desenhamos, produzimos e entregamos o seu carrinho chave-na-mão. Envie-nos a sua ideia e respondemos em 48h.",
                            "We design, build and deliver your turnkey cart. Send us your idea and we reply within 48h."
                        )}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/simulador"
                            data-testid="galeria-cta-simulador"
                            className="btn-premium-gold inline-flex items-center gap-2"
                        >
                            {tr("Configurar o meu carrinho", "Configure my cart")}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link
                            to="/contacto"
                            data-testid="galeria-cta-contacto"
                            className="inline-flex items-center gap-2 rounded-full border-2 border-primary/15 bg-white px-6 py-3 text-sm font-bold text-primary hover:bg-muted transition"
                        >
                            {tr("Pedir orçamento", "Request a quote")}
                        </Link>
                    </div>
                </div>
            </section>

            {/* LIGHTBOX */}
            {current && (
                <div
                    data-testid="galeria-lightbox"
                    className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closeLightbox}
                    role="dialog"
                    aria-label={current.brand}
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        aria-label={tr("Fechar", "Close")}
                        data-testid="lightbox-close"
                        className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white inline-flex items-center justify-center transition"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    {filtered.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); prev(); }}
                                aria-label={tr("Anterior", "Previous")}
                                data-testid="lightbox-prev"
                                className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white inline-flex items-center justify-center transition"
                            >
                                <ChevronLeft className="h-6 w-6" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); next(); }}
                                aria-label={tr("Seguinte", "Next")}
                                data-testid="lightbox-next"
                                className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white inline-flex items-center justify-center transition"
                            >
                                <ChevronRight className="h-6 w-6" />
                            </button>
                        </>
                    )}

                    <div
                        className="max-w-6xl w-full max-h-[calc(100vh-2rem)] flex flex-col md:flex-row items-stretch gap-0 rounded-2xl overflow-hidden bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="md:w-3/5 bg-black flex items-center justify-center">
                            <img
                                src={current.image_url}
                                alt={current.brand}
                                className="w-full h-full max-h-[70vh] md:max-h-[85vh] object-contain"
                            />
                        </div>
                        <div className="md:w-2/5 p-6 lg:p-8 flex flex-col overflow-y-auto max-h-[45vh] md:max-h-[85vh]">
                            {current.category && (
                                <span className="inline-flex self-start items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary mb-4">
                                    {categoryLabel(current.category, tr)}
                                </span>
                            )}
                            <h3 className="font-heading text-2xl lg:text-3xl font-extrabold text-primary leading-tight">
                                {current.brand}
                            </h3>
                            {current.title && (
                                <div className="mt-1 text-secondary font-bold text-sm uppercase tracking-wider">
                                    {current.title}
                                </div>
                            )}
                            {current.description && (
                                <p className="mt-5 text-muted-foreground leading-relaxed text-sm">
                                    {current.description}
                                </p>
                            )}

                            {/* CLIENT TESTIMONIAL (quote) */}
                            {current.quote && (
                                <blockquote
                                    data-testid="lightbox-quote"
                                    className="mt-6 rounded-xl border-l-4 border-secondary bg-muted/60 pl-5 pr-4 py-4 relative"
                                >
                                    <Quote className="absolute -top-3 left-3 h-6 w-6 text-secondary bg-white rounded-full p-1" />
                                    <p className="text-sm italic text-primary/90 leading-relaxed">
                                        “{current.quote}”
                                    </p>
                                    {current.client_name && (
                                        <footer className="mt-3 text-xs font-bold text-primary/70">
                                            — {current.client_name}
                                        </footer>
                                    )}
                                </blockquote>
                            )}

                            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                                {current.location && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5" /> {current.location}
                                    </span>
                                )}
                                {current.year && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar className="h-3.5 w-3.5" /> {current.year}
                                    </span>
                                )}
                            </div>

                            {/* SHARE ROW */}
                            {current.slug && (
                                <div className="mt-5 flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => copyLink(current.slug)}
                                        data-testid="lightbox-copy-link"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary hover:bg-muted transition"
                                    >
                                        {copiedSlug === current.slug ? (
                                            <><Check className="h-3.5 w-3.5 text-emerald-600" /> {tr("Copiado", "Copied")}</>
                                        ) : (
                                            <><LinkIcon className="h-3.5 w-3.5" /> {tr("Copiar link", "Copy link")}</>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => shareOnWhatsApp(current)}
                                        data-testid="lightbox-share-whatsapp"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 transition"
                                    >
                                        <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                                    </button>
                                </div>
                            )}

                            <div className="mt-auto pt-6">
                                <Link
                                    to="/simulador"
                                    onClick={closeLightbox}
                                    className="btn-premium-gold w-full text-center inline-flex items-center justify-center gap-2"
                                >
                                    {tr("Quero um assim", "I want one like this")}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function categoryLabel(cat, tr) {
    const c = CATEGORIES.find(x => x.id === cat);
    if (!c) return cat;
    return tr(c.label_pt, c.label_en);
}

function GalleryCard({ ev, onOpen }) {
    const cat = CATEGORIES.find(c => c.id === ev.category);
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`gallery-item-${ev.id}`}
            className="mb-5 block w-full break-inside-avoid rounded-2xl border border-border bg-white overflow-hidden text-left transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:border-secondary/40 group"
        >
            {ev.image_url ? (
                <div className="relative overflow-hidden bg-muted">
                    <img
                        src={ev.image_url}
                        alt={ev.brand}
                        loading="lazy"
                        className="w-full h-auto object-cover transition duration-500 group-hover:scale-105"
                    />
                    {cat && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary shadow-sm">
                            <span aria-hidden>{cat.emoji}</span>
                        </span>
                    )}
                    {ev.quote && (
                        <span
                            title="Este caso tem testemunho de cliente"
                            className="absolute top-3 right-3 inline-flex items-center justify-center h-7 w-7 rounded-full bg-secondary text-primary shadow-sm"
                        >
                            <Quote className="h-3.5 w-3.5" />
                        </span>
                    )}
                </div>
            ) : (
                <div className="aspect-[4/3] bg-muted" />
            )}
            <div className="p-4">
                <div className="font-heading text-base font-extrabold text-primary leading-tight">
                    {ev.brand}
                </div>
                {ev.title && (
                    <div className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                        {ev.title}
                    </div>
                )}
            </div>
        </button>
    );
}
